// imports
import { AnyAction } from '@reduxjs/toolkit';
import { useSelector, useDispatch } from 'react-redux';
import React, {
  useRef, useState,
  useEffect, useMemo
} from 'react';

import EmptyList from './empty';
import Topbar from '../tabView/topbar';
import Utils from '../../common/utils';
import { TEXTS } from '../../constants';
import HeroCard from '../../atoms/card/hero';
import Dropdown from '../../atoms/dropdown/list';
import RefreshBtn from '../../atoms/button/refresh';
import { HeroNFT } from '../../repositories/web3/types';
import TraitListTooltip from '../../atoms/tooltip/traitList';
import { fetchHeroes } from '../../repositories/store/thunks';
import ScrollableContainer from '../../atoms/container/scrollable';
import { selectConfig, selectUserHeroes, selectUserQuests } from '../../repositories/store/slices';
import { ITraitMatching, IScrollableContainer, HeroListFilter as Filter } from '../../common/types';

// interfaces / types / consts
type SortBy = 'id' | 'name' | 'species' | 'traits';
type HeroCB = (hero: HeroNFT | undefined, filter: Filter) => void;
interface Props {
  slotTraits?: string[];
  selectedHero?: HeroNFT;
  selectedCrew?: number[];
  disableLocked?: boolean;
  crewTraitMatching?: ITraitMatching[][];
  heroClickedCB?: HeroCB;
  heroDraggedCB?: HeroCB;
  filterChangedCB?: HeroCB;
}

// exports
export default function HeroList(props: Props): JSX.Element {
  const {
    slotTraits, disableLocked,
    selectedHero, selectedCrew, crewTraitMatching,
    heroClickedCB, filterChangedCB, heroDraggedCB
  } = props;
  // hooks configuration
  const dispatch = useDispatch();
  const isInitialMount = useRef(true);
  const { texts } = useSelector(selectConfig);
  const speciesFilterRef = useRef<Filter>('all');
  const { startId } = useSelector(selectUserQuests);
  const containerRef = useRef<IScrollableContainer>(null);
  const [speciesFilter, setSpeciesFilter] = useState<Filter>('all');
  const { nfts: heroNFTs, fetchStatus } = useSelector(selectUserHeroes);
  const [sortOrder, setSortOrder] = useState<SortBy>(slotTraits?.length ? 'traits' : 'id');

  const activeHeroes = heroNFTs.filter((heroNFT) => heroNFT.chain === 'L2');
  const heroesTraitMatching = activeHeroes.reduce((traitMatching, hero) => {
    traitMatching[hero.tokenId] = Utils.quest.getTraitMatching(hero.traitIds, slotTraits).heroes;
    return traitMatching;
  }, {} as { [key: number]: ITraitMatching[] });
  const filteredNFTs = useMemo(() => {
    const filtered = (speciesFilter === 'all') ? [...activeHeroes]
      : activeHeroes.filter((nft: HeroNFT) => nft.traitIds!.filter((trait) => trait.includes(speciesFilter)).length);
    if (sortOrder === 'name') {
      filtered.sort((a, b) => (a.name! < b.name! ? -1 : 0));
    } else if (sortOrder === 'species') {
      filtered.sort((a, b) => (Utils.hero.getSpecies(a.traitIds)
        < Utils.hero.getSpecies(b.traitIds) ? -1 : 0));
    } else if (sortOrder === 'traits') {
      filtered.sort((a, b) => {
        const aMatches = heroesTraitMatching[a.tokenId].filter((traitMatching) => traitMatching.status === 'success').length;
        const bMatches = heroesTraitMatching[b.tokenId].filter((traitMatching) => traitMatching.status === 'success').length;
        return bMatches - aMatches;
      });
    } else {
      filtered.sort((a, b) => a.tokenId - b.tokenId);
    }
    const locked = filtered.filter((nft) => nft.locked);
    const unLocked = filtered.filter((nft) => !nft.locked);
    return unLocked.concat(locked);
  }, [activeHeroes, heroesTraitMatching, speciesFilter, sortOrder]);

  const sortOptions = [{ key: 'ID', onClick: () => { setSortOrder('id'); } }, { key: 'Name', onClick: () => { setSortOrder('name'); } }];
  if (slotTraits?.length) { sortOptions.unshift({ key: 'Traits', onClick: () => { setSortOrder('traits'); } }); }
  if (speciesFilter === 'all') { sortOptions.push({ key: 'Species', onClick: () => { setSortOrder('species'); } }); }

  // private methods
  const handleCB = (nft?: HeroNFT, cb?: HeroCB): void => { if (cb) cb(nft, speciesFilter); };
  const handleTabClick = (filter: Filter): void => {
    setSpeciesFilter(filter);
    containerRef.current?.resetScroll();
  };

  // componentDidMount
  useEffect(() => {
    if (fetchStatus === 'idle') {
      dispatch(fetchHeroes() as unknown as AnyAction);
    } else if (fetchStatus === 'success') {
      if (speciesFilter !== 'all' && selectedHero && !speciesFilter.includes(Utils.hero.getSpecies(selectedHero.traitIds))) {
        handleCB(filteredNFTs[0], filterChangedCB);
      } else if (!selectedHero) {
        if (isInitialMount.current || speciesFilterRef.current !== speciesFilter) {
          isInitialMount.current = false;
          handleCB(filteredNFTs[0], filterChangedCB);
        }
      }
    }
    speciesFilterRef.current = speciesFilter;
  });
  const filteredTraits: { [key: number]: { matchedTraitsCount: number; sortedTraits: ITraitMatching[] } } = {};
  filteredNFTs.forEach((nft) => {
    const slotInCrew = selectedCrew ? selectedCrew.findIndex((id) => id === nft.tokenId) : -1;
    let traitMatching = slotTraits?.length ? heroesTraitMatching[nft.tokenId] : [];
    if (crewTraitMatching && slotInCrew > -1) { traitMatching = crewTraitMatching[slotInCrew]; }
    let matchedTraitsCount = 3;
    const hasMatching = Boolean(traitMatching?.length);
    const sortedTraits: ITraitMatching[] = hasMatching ? traitMatching!
      : nft.traitIds!.map((trait) => ({ id: trait, icon: trait.toUpperCase(), status: 'idle' }));
    if (hasMatching) { matchedTraitsCount = sortedTraits.filter((traitmatch) => traitmatch.status === 'success').length; }
    filteredTraits[nft.tokenId] = { sortedTraits, matchedTraitsCount };
  });

  let emptyText = '';
  if (fetchStatus === 'loading') {
    emptyText = TEXTS.LIST_LOADING;
  } else if (fetchStatus === 'error') {
    emptyText = TEXTS.LIST_FAILED_TO_FETCH;
  }
  const validInventory = Boolean(filteredNFTs.length);
  // render
  return (
    <div className="hero-list">
      <Topbar
        tabs={[
          { key: 'all', onClick: () => { handleTabClick('all'); } },
          { key: 'human', onClick: () => { handleTabClick('species_human'); } },
          { key: 'elf', onClick: () => { handleTabClick('species_elf'); } },
          { key: 'emberling', onClick: () => { handleTabClick('species_emberling'); } },
          { key: 'dragonkin', onClick: () => { handleTabClick('species_dragonkin'); } }
        ]}
      />
      <ScrollableContainer
        ref={containerRef}
        fixed={(
          <div className="fixed">
            <Dropdown items={sortOptions} />
            <RefreshBtn
              isLoading={fetchStatus === 'loading'}
              onClick={() => {
                isInitialMount.current = true;
                handleCB(undefined, filterChangedCB);
                dispatch(fetchHeroes() as unknown as AnyAction);
              }}
            />
          </div>
        )}
      >
        {!validInventory ? <EmptyList text={emptyText} linkType={heroNFTs.length > 0 && activeHeroes.length === 0 ? 'heroreserve' : 'opensea'} /> : (
          <div>
            {filteredNFTs.map((nft) => (
              <HeroCard
                nft={nft}
                key={nft.tokenId}
                onDrag={() => handleCB(nft, heroDraggedCB)}
                onClicked={() => handleCB(nft, heroClickedCB)}
                selected={selectedHero?.tokenId === nft.tokenId}
                disabled={(disableLocked && nft.locked) || (Boolean(startId))}
                isCrewMember={selectedCrew?.length ? selectedCrew.includes(nft.tokenId) : undefined}
                traitMatching={filteredTraits[nft.tokenId]?.sortedTraits?.slice(0, filteredTraits[nft.tokenId]?.matchedTraitsCount)}
              />
            ))}
          </div>
        )}
      </ScrollableContainer>
      {filteredNFTs.map((nft) => (
        <TraitListTooltip
          key={`#hero-traits-${nft.tokenId}`}
          title={texts.tooltip_traits_info.title}
          anchorSelect={`#hero-traits-${nft.tokenId}`}
          description={texts.tooltip_traits_info.body}
          traits={filteredTraits[nft.tokenId]?.sortedTraits}
        />
      ))}
    </div>
  );
}

// default props
HeroList.defaultProps = {
  slotTraits: [],
  selectedCrew: [],
  disableLocked: false,
  crewTraitMatching: [],
  selectedHero: undefined,
  heroClickedCB: undefined,
  heroDraggedCB: undefined,
  filterChangedCB: undefined
};
